import { MS_IN_SECOND, SECONDS_IN_MINUTE } from "./constants";

const MIN_ACCURACY = 10000000;
//Borrowed from avl
export class LocationTracker {
	private watchId: number = null;
	private mostRecent: GeolocationPosition;
	private mostRecentHighAccuracy: GeolocationPosition;
	private trackingStarted: boolean = null;
	private pendingStart: Promise<GeolocationPosition> = null;
	permissionError: GeolocationPositionError;
	private readonly resolveFunctions = new Map<string, (coords: GeolocationCoordinates) => void>();

	constructor(
		private highAccuracy = true,
		private isDev = !!(import.meta as any).env?.DEV,
	) {}

	public addResolveFunction(functionKey: string, newFunction: (coords: GeolocationCoordinates) => void) {
		this.resolveFunctions.set(functionKey, newFunction);
	}

	public removeResolveFunction(functionKey: string) {
		this.resolveFunctions.delete(functionKey);
	}

	/**
	 * Can't apply spread operator to GeoLocationPosition/Coordinate, so this function pulls the important stuff out
	 */
	static getCoordinates(position: GeolocationPosition) {
		if (!position) return null;
		const { coords } = position;
		if (!coords) return null;
		return {
			latitude: coords.latitude,
			longitude: coords.longitude,
			accuracy: coords.accuracy,
		} as GeolocationCoordinates;
	}

	/**
	 * Returns the most recent position if tracking, waits for a start that is still in progress,
	 * and otherwise starts tracking
	 */
	async continueTracking() {
		if (this.trackingStarted) {
			const mostRecent = this.mostRecentPosition();
			if (mostRecent) return mostRecent;
			if (this.pendingStart) return await this.pendingStart;
		}
		return await this.startTracking();
	}

	/**
	 * Initiates GPS tracking, and returns promise that fires when tracking is started (or fails to start)
	 */
	startTracking() {
		this.stopTracking();
		this.trackingStarted = true;
		const setupPromise = new Promise<GeolocationPosition>((resolve, reject) => {
			if (LocationTracker.canTrack()) this.watchPosition(resolve, reject);
			else reject("No GPS");
		});

		this.pendingStart = setupPromise;
		const settle = () => {
			if (this.pendingStart === setupPromise) this.pendingStart = null;
		};
		setupPromise.then(settle, settle);

		return setupPromise;
	}

	/** Return truish if can track location */
	static canTrack() {
		return navigator.geolocation;
	}

	private watchPosition(resolve: PositionCallback, reject: (error: string) => void) {
		const onSuccess: PositionCallback = (pos) => {
			this.mostRecent = pos;
			if (pos.coords.accuracy < MIN_ACCURACY) this.mostRecentHighAccuracy = pos;
			else if (!capturedRecently(this.mostRecentHighAccuracy, 5 * SECONDS_IN_MINUTE))
				this.mostRecentHighAccuracy = null;
			for (const resolveFunction of Array.from(this.resolveFunctions.values())) resolveFunction(pos.coords);
			resolve(pos);
		};

		const onFail: PositionErrorCallback = (error) => {
			if (!capturedRecently(this.mostRecent, 10 * SECONDS_IN_MINUTE)) {
				this.mostRecent = null;
				this.mostRecentHighAccuracy = null;
			}
			this.permissionError = error;

			if (this.isDev) {
				onSuccess({
					coords: {
						latitude: 38.88101,
						longitude: -77.10428,
						accuracy: 10,
						altitude: null,
						altitudeAccuracy: null,
						heading: null,
						speed: null,
					} as GeolocationCoordinates,
					timestamp: Date.now(),
				} as GeolocationPosition);
				return;
			}

			switch (error.code) {
				case GeolocationPositionError.PERMISSION_DENIED:
					reject("Declined GPS Positioning");
					break;
				case GeolocationPositionError.POSITION_UNAVAILABLE:
					reject("Unable to get GPS Position");
					break;
				case GeolocationPositionError.TIMEOUT:
					reject("GPS Positioning Timed Out");
					break;
			}
		};

		//On success called every time gets position
		this.watchId = navigator.geolocation.watchPosition(onSuccess, onFail, {
			enableHighAccuracy: this.highAccuracy,
			maximumAge: 30000,
			timeout: 27000,
		});
	}

	/**
	 * Stops tracking position
	 */
	stopTracking() {
		this.pendingStart = null;
		if (!this.watchId) return;

		navigator.geolocation.clearWatch(this.watchId);

		this.watchId = null;
	}

	/**
	 * Returns most recent position that matches a set standard of accuracy
	 * Accuracy is in meters, lower is more accurate
	 */
	mostRecentPosition() {
		return this.mostRecentHighAccuracy ?? this.mostRecent;
	}

	consentedToGps() {
		if (this.permissionError)
			return this.trackingStarted && this.permissionError.code !== GeolocationPositionError.PERMISSION_DENIED;
		else return this.trackingStarted;
	}
}

/** Returns true if geoposition was captured within s seconds */
function capturedRecently(pos: GeolocationPosition, s: number) {
	return !!pos && pos.timestamp + s * MS_IN_SECOND > Date.now();
}
